/**
 * GS1 Table 7-20 (82-character set — XCHAR; excludes '#')
 * GS1 General Specifications Release 26.0 Table 7-20, p551;
 * GS1 Digital Link Standard: URI Syntax v1.7.0 §4.2, p22.
 */
const XCHAR_SET = new Set('!"%&\'()*+,-./0123456789:;<=>?ABCDEFGHIJKLMNOPQRSTUVWXYZ_abcdefghijklmnopqrstuvwxyz'.split(''));
/**
 * GS1 Table 7-21 (39-character set — YCHAR for CPID 8010; includes '#')
 * GS1 General Specifications Release 26.0 Table 7-21, p552;
 * GS1 Digital Link Standard: URI Syntax v1.7.0 §4.2, p22.
 */
const YCHAR_SET = new Set('#-/0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(''));
export const NUMERIC_REGEX = /^[0-9]+$/;
/**
 * ZCHAR (64-character base64url + '=' set for Digital Signature 8030)
 * GS1 Digital Link Standard: URI Syntax v1.7.0 §4.2, p22 & §4.10, p31.
 */
const DIGSIG_REGEX = /^[A-Za-z0-9_\-=]+$/;
/**
 * Characters that MUST be percent-encoded when appearing inside AI values in GS1 Digital Link URIs
 * (GS1 Digital Link Standard: URI Syntax v1.7.0 §4.2, pp21–22).
 */
const PERCENT_ENCODE_MAP = {
    '#': '%23',
    '/': '%2F',
    '%': '%25',
    '&': '%26',
    '+': '%2B',
    ',': '%2C',
    '!': '%21',
    '(': '%28',
    ')': '%29',
    '*': '%2A',
    "'": '%27',
    ':': '%3A',
    ';': '%3B',
    '<': '%3C',
    '=': '%3D',
    '>': '%3E',
    '?': '%3F',
};
const RCN_GTIN13_PREFIXES = new Set([
    '02',
    '04',
    '20',
    '21',
    '22',
    '23',
    '24',
    '25',
    '26',
    '27',
    '28',
    '29',
]);
/**
 * Calculates the GS1 mod-10 check digit for a numeric payload (without the check digit).
 * Clause: GS1 General Specifications Release 26.0 §7.9
 */
export function calculateCheckDigit(digitsWithoutCheck) {
    if (!NUMERIC_REGEX.test(digitsWithoutCheck)) {
        throw new Error('calculateCheckDigit requires numeric digits only');
    }
    let sum = 0;
    const len = digitsWithoutCheck.length;
    for (let i = 0; i < len; i++) {
        const char = digitsWithoutCheck[len - 1 - i];
        const digit = Number(char);
        const weight = i % 2 === 0 ? 3 : 1;
        sum += digit * weight;
    }
    const mod = sum % 10;
    return String(mod === 0 ? 0 : 10 - mod);
}
/**
 * Verifies the GS1 mod-10 check digit on a complete numeric string where the final digit is the check digit.
 * Clause: GS1 General Specifications Release 26.0 §7.9
 */
export function verifyCheckDigit(fullNumericString) {
    if (fullNumericString.length < 2 || !NUMERIC_REGEX.test(fullNumericString)) {
        return false;
    }
    const payload = fullNumericString.slice(0, -1);
    const expected = calculateCheckDigit(payload);
    return fullNumericString.endsWith(expected);
}
/**
 * Checks whether a GTIN (8, 12, 13, or 14 digits) is a Restricted Circulation Number (RCN).
 * Consistent across 12-, 13-, and 14-digit representations of the same GTIN.
 * Clause: GS1 General Specifications Release 26.0 §1.2.2.2.1, p24; Table 1-4, p26; Table 1-5, p27
 */
export function isRestrictedCirculationGtin(rawGtin, paddedGtin14) {
    // Check GTIN-8 RCN prefixes (Table 1-5: 000–099 and 200–299)
    // Applies when input is 8 digits OR when 14-digit GTIN has 6 leading zeros (000000xxxxxxxx)
    if (rawGtin.length === 8 || paddedGtin14.startsWith('000000')) {
        const gtin8Prefix = Number(paddedGtin14.slice(6, 9));
        if ((gtin8Prefix >= 0 && gtin8Prefix <= 99) || (gtin8Prefix >= 200 && gtin8Prefix <= 299)) {
            return true;
        }
        return false;
    }
    // Check GTIN-13 RCN prefixes (Table 1-4: 02, 04, 20–29 in positions 2..3 of GTIN-14)
    // Applies consistently whether input was 12, 13, or 14 digits.
    const gtin13Prefix = paddedGtin14.slice(1, 3);
    if (RCN_GTIN13_PREFIXES.has(gtin13Prefix)) {
        return true;
    }
    return false;
}
/**
 * Percent-encodes reserved GS1 characters for canonical URI construction.
 * Clause: GS1 Digital Link Standard: URI Syntax v1.7.0 §4.2, pp21–22
 */
export function encodeCanonicalGs1Value(decodedValue) {
    let out = '';
    for (const ch of decodedValue) {
        const mapped = PERCENT_ENCODE_MAP[ch];
        out += mapped !== undefined ? mapped : ch;
    }
    return out;
}
/**
 * Decodes percent-encoded segments and validates character set membership.
 */
export function decodeGs1Segment(rawSegment) {
    try {
        return { ok: true, value: decodeURIComponent(rawSegment) };
    }
    catch {
        return { ok: false };
    }
}
export function isValidCharset(value, charset) {
    if (value.length === 0)
        return false;
    if (charset === 'NUMERIC') {
        return NUMERIC_REGEX.test(value);
    }
    if (charset === 'DIGSIG') {
        return DIGSIG_REGEX.test(value);
    }
    const set = charset === 'XCHAR' ? XCHAR_SET : YCHAR_SET;
    for (const ch of value) {
        if (!set.has(ch)) {
            return false;
        }
    }
    return true;
}
/**
 * Validates a 6-digit YYMMDD GS1 Date Application Identifier (11, 12, 13, 15, 16, 17, 4326, 7006).
 * Permits DD=00 as "last day of month" per GS1 General Specifications Release 26.0 §3.4.7.
 */
export function isValidGs1DateAi(yymmdd) {
    if (yymmdd.length !== 6 || !NUMERIC_REGEX.test(yymmdd)) {
        return false;
    }
    const yy = Number(yymmdd.slice(0, 2));
    const mm = Number(yymmdd.slice(2, 4));
    const dd = Number(yymmdd.slice(4, 6));
    if (mm < 1 || mm > 12) {
        return false;
    }
    if (dd === 0) {
        return true;
    }
    const fullYear = 2000 + yy;
    const isLeap = (fullYear % 4 === 0 && fullYear % 100 !== 0) || fullYear % 400 === 0;
    const daysInMonths = [31, isLeap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    const maxDay = daysInMonths[mm - 1] ?? 31;
    return dd >= 1 && dd <= maxDay;
}
//# sourceMappingURL=check-digit.js.map